import React, { useState, useEffect } from 'react';
import { Linkedin, Mail, TrendingUp, TrendingDown, Minus, Sun, Moon, Menu, X, ArrowLeft } from 'lucide-react';
import { careerTimeline, categories, duplicatedSkills, interests } from '../data/portfolioContent';

const NewsletterPortfolio = () => {
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      return savedTheme === 'dark';
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showPortfolioPage, setShowPortfolioPage] = useState(false);

  useEffect(() => {
    const html = document.documentElement;
    if (isDarkMode) {
      html.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      html.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prevMode => !prevMode);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(prev => !prev);
  };

  const handleShowPortfolio = () => {
    setShowPortfolioPage(true);
    setIsMobileMenuOpen(false);
  };

  const handleHidePortfolio = () => {
    setShowPortfolioPage(false);
  };

  return (
    <div className="min-h-screen bg-white text-gray-900 dark:bg-gray-900 dark:text-white font-sans text-sm sm:text-base">
      <style>
        {`
        @keyframes scroll-left {
          0% {
            transform: translateX(0%);
          }
          100% {
            transform: translateX(-33.333%);
          }
        }
        .animate-scroll-left {
          animation: scroll-left 30s linear infinite;
        }
        `}
      </style>

      {showPortfolioPage ? (
        <div className="min-h-screen bg-white text-gray-900 dark:bg-gray-900 dark:text-white flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-4xl sm:text-5xl font-bold mb-4 text-blue-700 dark:text-blue-400">🚧 Work in Progress</h1>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-300 mb-6 max-w-md mx-auto">
              I'm currently building out my portfolio content. More exciting work will be available here soon — stay tuned!
            </p>
            <button
              onClick={handleHidePortfolio}
              className="inline-flex items-center bg-blue-700 text-white px-4 py-2 rounded-full font-medium text-sm hover:bg-blue-800 transition duration-200 shadow"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </button>
          </div>
        </div>
      ) : (
        <>
          {/* Top Navigation */}
          <nav className="border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm relative overflow-hidden">
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
              <div className="flex justify-between items-center h-14 text-xs sm:text-sm">
                <div className="hidden md:flex space-x-4 sm:space-x-6">
                  <button className="text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400 pb-1 font-semibold">Today's Profile</button>
                  <button className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Experience</button>
                  <button
                    className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200"
                  >
                    Projects
                  </button>
                  <button className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Blog</button>
                  <button className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Publications</button>
                  <button className="text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 transition-colors duration-200">Contact</button>
                </div>

                <div className="md:hidden flex items-center">
                  <button
                    onClick={toggleMobileMenu}
                    className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  >
                    {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                  </button>
                </div>

                <div className="flex space-x-3">
                  <button
                    onClick={toggleDarkMode}
                    className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium flex items-center"
                    aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                    title={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
                  >
                    {isDarkMode ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                  </button>
                  <a
                    href="https://www.linkedin.com/in/deanfellner/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white font-medium flex items-center"
                  >
                    Connect
                  </a>
                </div>
              </div>
            </div>

            {isMobileMenuOpen && (
              <div className="md:hidden pb-4 pt-2 border-t border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 absolute top-full left-0 right-0 z-20 shadow-lg">
                <div className="px-4 space-y-2">
                  <button className="block w-full text-left text-blue-600 dark:text-blue-400 py-2 pl-3 pr-4 border-l-4 border-blue-600 dark:border-blue-400 font-semibold">Today's Profile</button>
                  <button className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 py-2 pl-3 pr-4 transition-colors duration-200">Experience</button>
                  <button
                    className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 py-2 pl-3 pr-4 transition-colors duration-200"
                  >
                    Projects
                  </button>
                  <button className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 py-2 pl-3 pr-4 transition-colors duration-200">Blog</button>
                  <button className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 py-2 pl-3 pr-4 transition-colors duration-200">Publications</button>
                  <button className="block w-full text-left text-gray-700 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400 py-2 pl-3 pr-4 transition-colors duration-200">Contact</button>
                </div>
              </div>
            )}
          </nav>

          {/* Header */}
          <header className="bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
            <div className="max-w-screen-xl mx-auto px-4 py-6 sm:py-8">
              <div className="text-center">
                <h1 className="text-4xl sm:text-6xl font-extrabold text-blue-700 dark:text-blue-400 mb-1 font-serif tracking-tight">
                  DEAN FELLNER
                </h1>
                <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-400 italic">Where Finance Meets Technology</p>
              </div>
            </div>
          </header>

          {/* Categories Section */}
          <nav className="bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-200 py-2 border-b border-gray-200 dark:border-gray-700 shadow-sm">
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 overflow-x-auto whitespace-nowrap scrollbar-hide">
              <div className="flex justify-center space-x-5 text-xs sm:text-sm">
                {categories.map((category, index) => (
                  <span
                    key={index}
                    className="py-1.5 px-2.5 rounded-md font-medium text-gray-700 dark:text-gray-300">
                    {category}
                  </span>
                ))}
              </div>
            </div>
          </nav>

          {/* Blue Banner Section */}
          <div className="bg-blue-700 dark:bg-blue-800 text-white py-2.5 px-4 sm:px-6 lg:px-8 text-center">
            <div className="max-w-screen-xl mx-auto flex flex-col sm:flex-row justify-center items-center gap-2.5">
              <p className="text-xs sm:text-sm font-medium">
                EXPLORE MY LATEST WORK & INSIGHTS FOR FREE
              </p>
              <button
                onClick={handleShowPortfolio}
                className="bg-white text-blue-700 dark:bg-blue-200 dark:text-blue-900 px-3 py-1.5 rounded-full text-xs font-semibold hover:bg-blue-100 dark:hover:bg-blue-300 transition-colors duration-200 shadow">
                VIEW PORTFOLIO NOW <span className="ml-1">&gt;</span>
              </button>
            </div>
          </div>

          {/* Skills Ticker - Auto-scrolling */}
          <div className="bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 py-3 overflow-hidden">
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex animate-scroll-left w-max min-w-full">
                {duplicatedSkills.map((skill, index) => (
                  <div key={index} className="flex items-center space-x-1.5 text-xs sm:text-sm bg-white dark:bg-gray-700 px-2.5 py-0.5 rounded-full shadow-sm flex-shrink-0 mx-2.5">
                    <span className="font-semibold text-gray-800 dark:text-gray-200">{skill.name}</span>
                    <span className="text-base font-bold text-blue-600 dark:text-blue-400">{skill.rating}</span>
                    <span className={`flex items-center text-xs ${
                      skill.trend === 'up' ? 'text-green-600' :
                      skill.trend === 'down' ? 'text-red-600' : 'text-gray-500 dark:text-gray-400'
                    }`}>
                      {skill.trend === 'up' ? <TrendingUp className="w-2.5 h-2.5 mr-0.5" /> :
                       skill.trend === 'down' ? <TrendingDown className="w-2.5 h-2.5 mr-0.5" /> :
                       <Minus className="w-2.5 h-2.5 mr-0.5" />}
                      {skill.change}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <main className="max-w-screen-xl mx-auto px-4 py-6 sm:py-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6 items-stretch">
              <div className="lg:col-span-2">
                <div className="p-5 bg-white dark:bg-gray-800 rounded-lg shadow-md h-full">
                  <span className="text-blue-600 dark:text-blue-400 text-xs font-medium uppercase tracking-wider">Featured Profile</span>
                  <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white mt-1.5 mb-3 leading-tight">
                    Can Finance Keep Up with the Future? Exploring AI, Innovation, and Impact in a Global FMCG
                  </h2>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-xs mt-4.5">
                    After finishing four years of study at UNSW, majoring in Finance and Financial Technology, I’ve always been drawn to the intersection of business, innovation, and impact. During my time at university, I explored everything from startups and venture capital to social entrepreneurship... driven by a curiosity for how systems work and how they can be improved.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-xs mt-3">
                    Now, as a finance graduate at one of the world’s largest FMCG companies, I’ve taken that mindset into the corporate world. I work across commercial finance teams to support decision making, manage performance, and drive business outcomes... but I’m also part of the AI Squad Champions, helping lead the charge on embedding emerging technologies into everyday processes.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-xs mt-3">
                    From streamlining reporting to unlocking insights through automation and AI, I’m focused on reshaping what finance can be... and asking the tough questions about how we future proof the function in an increasingly fast moving world.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed text-xs mt-3">
                    This is just the beginning and I’m energised by what’s ahead...
                  </p>
                </div>
              </div>

              <div className="lg:col-span-1">
                <div className="rounded-lg overflow-hidden shadow-lg w-full h-full">
                  <img src="/images/dean-headshot.jpeg" alt="Dean Fellner" className="w-full h-full object-cover" />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-md flex flex-col sm:flex-row items-center gap-3 h-full">
                  <div className="flex-grow">
                    <p className="text-blue-600 dark:text-blue-400 text-base font-semibold mb-1.5">Supply Chain Financial Analyst</p>
                    <p className="text-gray-700 dark:text-gray-300 text-xs mb-3">Unilever • 2025-Present</p>
                    <p className="text-gray-700 dark:text-gray-300 text-xs mb-3">
                        Currently serving as a Supply Chain Financial Analyst at one of the world’s largest FMCG companies. My responsibilities include supporting the reporting process and tracking the financial performance of manufacturing sites across Australia and New Zealand. I’m now expanding into a new area, taking on financial oversight for logistics, providing analysis and insights to help optimise one of the most critical parts of the supply chain.
                    </p>
                    <div className="flex flex-wrap gap-1.5 justify-center">
                      <span className="bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-100 px-2 py-0.5 rounded-full text-xs font-medium">Financial Analysis</span>
                      <span className="bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-100 px-2 py-0.5 rounded-full text-xs font-medium">FMCG</span>
                      <span className="bg-purple-100 text-purple-800 dark:bg-purple-700 dark:text-purple-100 px-2 py-0.5 rounded-full text-xs font-medium">Supply Chain</span>
                    </div>
                  </div>
                  <div className="flex-shrink-0 w-28 h-20 flex items-center justify-center">
                    <img src="/images/unilever.svg" alt="Unilever logo" className="w-full h-full object-contain" />
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-md h-full flex flex-col justify-center">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">My Interests</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                    {interests.map((interest, index) => (
                      <span key={index} className="bg-indigo-100 text-indigo-800 dark:bg-indigo-700 dark:text-indigo-100 px-2.5 py-0.5 rounded-full text-xs font-medium text-center">
                        {interest}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-2">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-md h-full">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-5">Career Journey</h3>
                  <div className="flex justify-between items-start relative pt-5 overflow-x-auto pb-3">
                    <div className="absolute top-7 left-0 right-0 h-0.5 bg-gray-300 dark:bg-gray-600 mx-auto w-full"></div>
                    {careerTimeline.map((role, index) => (
                      <div key={index} className="flex flex-col items-center text-center z-10 flex-shrink-0 w-[calc(100%/6)] px-1.5">
                        <div className={`w-3.5 h-3.5 rounded-full ${role.active ? 'bg-blue-600' : 'bg-gray-400 dark:bg-gray-500'} flex-shrink-0 mb-1.5 mt-0`}></div>
                        <h4 className="font-bold text-gray-900 dark:text-white text-xs">{role.title}</h4>
                        <p className="text-gray-600 dark:text-gray-300 text-xs">{role.company}</p>
                        <p className="text-gray-600 dark:text-gray-300 text-xs">{role.years}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="lg:col-span-1">
                <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-5 shadow-md h-full flex flex-col justify-center">
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-3">My Purpose Statement</h3>
                  <p className="text-gray-700 dark:text-gray-300 text-sm leading-relaxed">
                    I am eager to drive meaningful impact by integrating finance with technology, delivering practical, innovative solutions that enhance business efficiency and sustainable growth.
                  </p>
                </div>
              </div>
            </div>

            <div className="mt-10 bg-blue-700 dark:bg-blue-900 rounded-lg p-6 text-white text-center shadow-lg">
              <h2 className="text-2xl font-bold mb-3">Ready to Connect?</h2>
              <p className="text-blue-100 dark:text-blue-200 text-base mb-5 max-w-xl mx-auto">
                Whether you have a project idea, a collaboration opportunity, or just want to chat about finance or technology, I'd love to hear from you!
              </p>
              <div className="flex flex-col sm:flex-row justify-center items-center gap-3">
                <a
                  href="mailto:deanfellnerhq@gmail.com"
                  className="bg-white text-blue-600 dark:bg-blue-200 dark:text-blue-900 px-6 py-2.5 rounded-full text-base font-semibold hover:bg-blue-100 dark:hover:bg-blue-300 transition-colors duration-200 shadow-md flex items-center justify-center"
                >
                  <Mail className="w-4 h-4 mr-1.5" /> Email Me
                </a>
                <a
                  href="https://www.linkedin.com/in/deanfellner/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-blue-800 text-white dark:bg-blue-600 dark:text-white px-6 py-2.5 rounded-full text-base font-semibold hover:bg-blue-900 dark:hover:bg-blue-700 transition-colors duration-200 shadow-md flex items-center justify-center"
                >
                  <Linkedin className="w-4 h-4 mr-1.5" /> Connect on LinkedIn
                </a>
              </div>
            </div>
          </main>

          {/* Footer */}
          <footer className="bg-white dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 mt-14 py-6">
            <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="text-center">
                <p className="text-gray-700 dark:text-gray-300 text-base font-medium mb-1.5">© 2025 Dean Fellner - Professional Website</p>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Built with React & Tailwind CSS | Deployed on Vercel</p>
              </div>
            </div>
          </footer>
        </>
      )}
    </div>
  );
};

export default NewsletterPortfolio;
